<template>
  <div class="tz-wrap">

    <!-- Header -->
    <div class="tz-header">
      <span class="tz-header__icon">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
          stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
      </span>
      <span class="tz-header__title">World Clock</span>
      <span class="tz-live-dot"></span>

      <!-- Mode toggle -->
      <div class="tz-mode-toggle">
        <button
          :class="['tz-mode-btn', { 'tz-mode-btn--active': viewMode === 'digital' }]"
          @click="viewMode = 'digital'"
          title="Digital"
        >
          <!-- display / digits icon -->
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="6" width="20" height="12" rx="2"/>
            <path d="M8 12h.01M12 12h.01M16 12h.01"/>
          </svg>
        </button>
        <button
          :class="['tz-mode-btn', { 'tz-mode-btn--active': viewMode === 'analog' }]"
          @click="viewMode = 'analog'"
          title="Analog"
        >
          <!-- clock icon -->
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- ═══════════ DIGITAL ═══════════ -->
    <transition name="tz-fade" mode="out-in">
      <div v-if="viewMode === 'digital'" key="digital" class="tz-card-outer">
        <div
          class="tz-card-ring"
          :style="{ '--accent': activeTz.accent, '--progress': ringProgressFor(activeTz.zone) }"
        >
          <div class="tz-card-inner">

            <!-- Left: time -->
            <div class="tz-clock">
              <div class="tz-clock__time">
                <span class="tz-hm">{{ hhmm }}</span>
                <div class="tz-clock__sub">
                  <span class="tz-ss">{{ ss }}<em>s</em></span>
                  <span class="tz-ampm">{{ ampm }}</span>
                </div>
              </div>
              <div class="tz-clock__date">{{ currentDate }}</div>
            </div>

            <!-- Divider -->
            <div class="tz-divider"></div>

            <!-- Right: tz selector + meta -->
            <div class="tz-right">
              <div class="tz-tabs">
                <button
                  v-for="tz in timezones"
                  :key="tz.key"
                  class="tz-tab"
                  :class="{ 'tz-tab--active': selectedTz === tz.key }"
                  :style="selectedTz === tz.key
                    ? { background: activeTz.accent + '22', color: activeTz.accent, borderColor: activeTz.accent + '66' }
                    : {}"
                  @click="selectedTz = tz.key"
                >
                  <span class="tz-tab__flag">{{ tz.flag }}</span>
                  <span>{{ tz.key }}</span>
                </button>
              </div>
              <div class="tz-meta">
                <span class="tz-city">{{ activeTz.city }}</span>
                <span class="tz-offset" :style="{ color: activeTz.accent }">{{ activeTz.offset }}</span>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- ═══════════ ANALOG ═══════════ -->
      <div v-else key="analog" class="tz-clocks-row">
        <div
          v-for="tz in timezones"
          :key="tz.key"
          class="tz-clock-item"
        >
          <!-- Ring + face -->
          <div
            class="tz-analog-outer"
            :style="{ '--accent': tz.accent, '--progress': ringProgressFor(tz.zone) }"
          >
            <svg class="tz-face" viewBox="0 0 200 200">
              <!-- Dial background -->
              <defs>
                <radialGradient :id="'dialGrad'+tz.key" cx="40%" cy="35%" r="65%">
                  <stop offset="0%"   stop-color="#f8fbff"/>
                  <stop offset="100%" stop-color="#e2ecf8"/>
                </radialGradient>
              </defs>
              <circle cx="100" cy="100" r="96" :fill="'url(#dialGrad'+tz.key+')'"/>

              <!-- Major ticks -->
              <line
                v-for="i in 12" :key="'M'+i"
                :x1="100 + 84 * Math.cos((i*30 - 90) * RAD)"
                :y1="100 + 84 * Math.sin((i*30 - 90) * RAD)"
                :x2="100 + 74 * Math.cos((i*30 - 90) * RAD)"
                :y2="100 + 74 * Math.sin((i*30 - 90) * RAD)"
                stroke="rgba(30,41,59,0.45)" stroke-width="2.5" stroke-linecap="round"
              />
              <!-- Minor ticks -->
              <template v-for="i in 60" :key="'m'+i">
                <line
                  v-if="i % 5 !== 0"
                  :x1="100 + 84 * Math.cos((i*6 - 90) * RAD)"
                  :y1="100 + 84 * Math.sin((i*6 - 90) * RAD)"
                  :x2="100 + 79 * Math.cos((i*6 - 90) * RAD)"
                  :y2="100 + 79 * Math.sin((i*6 - 90) * RAD)"
                  stroke="rgba(30,41,59,0.15)" stroke-width="1" stroke-linecap="round"
                />
              </template>

              <!-- Hour numbers -->
              <text
                v-for="h in 12" :key="'h'+h"
                :x="100 + 62 * Math.cos((h*30 - 90) * RAD)"
                :y="100 + 62 * Math.sin((h*30 - 90) * RAD)"
                text-anchor="middle" dominant-baseline="central"
                font-size="11" font-weight="700" fill="rgba(30,41,59,0.55)"
                font-family="system-ui,sans-serif"
              >{{ h }}</text>

              <!-- Hour hand -->
              <line x1="100" y1="100"
                :x2="100 + 36 * Math.cos((hourDeg(tz.zone) - 90) * RAD)"
                :y2="100 + 36 * Math.sin((hourDeg(tz.zone) - 90) * RAD)"
                stroke="#1e293b" stroke-width="5" stroke-linecap="round"/>
              <!-- Minute hand -->
              <line x1="100" y1="100"
                :x2="100 + 52 * Math.cos((minuteDeg(tz.zone) - 90) * RAD)"
                :y2="100 + 52 * Math.sin((minuteDeg(tz.zone) - 90) * RAD)"
                stroke="#334155" stroke-width="3" stroke-linecap="round"/>
              <!-- Second hand -->
              <line x1="100" y1="100"
                :x2="100 + 60 * Math.cos((secondDeg(tz.zone) - 90) * RAD)"
                :y2="100 + 60 * Math.sin((secondDeg(tz.zone) - 90) * RAD)"
                :stroke="tz.accent" stroke-width="1.5" stroke-linecap="round"/>
              <!-- Counter-weight -->
              <line x1="100" y1="100"
                :x2="100 - 16 * Math.cos((secondDeg(tz.zone) - 90) * RAD)"
                :y2="100 - 16 * Math.sin((secondDeg(tz.zone) - 90) * RAD)"
                :stroke="tz.accent" stroke-width="2.5" stroke-linecap="round"/>

              <!-- Center cap -->
              <circle cx="100" cy="100" r="5"   fill="#1e293b"/>
              <circle cx="100" cy="100" r="2.5" :fill="tz.accent"/>
            </svg>
          </div>

          <!-- Label -->
          <div class="tz-label">
            <span class="tz-label__key" :style="{ color: tz.accent }">{{ tz.flag }} {{ tz.key }}</span>
            <span class="tz-label__city">{{ tz.city }}</span>
            <span class="tz-label__time">{{ digitalTime(tz.zone) }}</span>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import moment from "moment-timezone";

const RAD = Math.PI / 180;

export default {
  name: "TimeZoneClock",
  data() {
    return {
      RAD,
      viewMode: "analog",   // 'digital' | 'analog'
      selectedTz: "IST",
      ticker: null,
      now: moment(),
      timezones: [
        { key: "IST", flag: "🇮🇳", zone: "Asia/Kolkata",        city: "India",      offset: "UTC +5:30", accent: "#0ea5e9" },
        { key: "EST", flag: "🇺🇸", zone: "America/New_York",    city: "New York",   offset: "UTC −5:00", accent: "#10b981" },
        { key: "PST",  flag: "🇺🇸", zone: "America/Los_Angeles", city: "California", offset: "UTC −8:00", accent: "#a855f7" },
      ],
    };
  },
  computed: {
    activeTz() {
      return this.timezones.find((t) => t.key === this.selectedTz);
    },
    tzM() {
      return this.now.clone().tz(this.activeTz.zone);
    },
    hhmm()        { return this.tzM.format("hh:mm"); },
    ss()          { return this.tzM.format("ss"); },
    ampm()        { return this.tzM.format("A"); },
    currentDate() { return this.tzM.format("ddd, MMM D · YYYY"); },
  },
  methods: {
    tzMoment(zone)          { return this.now.clone().tz(zone); },
    secondDeg(zone)         { return this.tzMoment(zone).seconds() * 6; },
    minuteDeg(zone)         { return this.tzMoment(zone).minutes() * 6 + this.tzMoment(zone).seconds() * 0.1; },
    hourDeg(zone)           { return (this.tzMoment(zone).hours() % 12) * 30 + this.tzMoment(zone).minutes() * 0.5; },
    ringProgressFor(zone)   { return (this.tzMoment(zone).seconds() / 60).toFixed(4); },
    digitalTime(zone)       { return this.tzMoment(zone).format("hh:mm A"); },
  },
  mounted() {
    this.ticker = setInterval(() => { this.now = moment(); }, 1000);
  },
  beforeUnmount() {
    clearInterval(this.ticker);
  },
};
</script>

<style scoped>
/* ── wrapper ── */
.tz-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  padding: 14px;
  box-sizing: border-box;
  user-select: none;
}

/* ── header ── */
.tz-header {
  display: flex;
  align-items: center;
  gap: 6px;
}
.tz-header__icon { color: #94a3b8; display: flex; align-items: center; }
.tz-header__title {
  flex: 1;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: #94a3b8;
}
.tz-live-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: #22c55e;
  animation: pulse-dot 2s infinite;
}
@keyframes pulse-dot {
  0%   { box-shadow: 0 0 0 0   rgba(34,197,94,.7); }
  70%  { box-shadow: 0 0 0 5px rgba(34,197,94, 0); }
  100% { box-shadow: 0 0 0 0   rgba(34,197,94, 0); }
}

/* ── mode toggle ── */
.tz-mode-toggle {
  display: flex;
  gap: 2px;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 2px;
}
.tz-mode-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px; height: 26px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  background: transparent;
  color: #94a3b8;
  transition: all 0.18s ease;
}
.tz-mode-btn:hover { background: #e2e8f0; color: #475569; }
.tz-mode-btn--active {
  background: #ffffff;
  color: #1e293b;
  box-shadow: 0 1px 4px rgba(0,0,0,0.12);
}

/* ── transition ── */
.tz-fade-enter-active,
.tz-fade-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.tz-fade-enter-from   { opacity: 0; transform: translateY(6px); }
.tz-fade-leave-to     { opacity: 0; transform: translateY(-6px); }

/* ══════════ DIGITAL ══════════ */
.tz-card-outer {
  border-radius: 20px;
  filter: drop-shadow(0 4px 16px rgba(0,0,0,0.14));
}
.tz-card-ring {
  border-radius: 20px;
  padding: 2.5px;
  background: conic-gradient(
    from -90deg,
    var(--accent)         0deg,
    var(--accent)         calc(var(--progress) * 360deg),
    rgba(30,41,59,0.10)   calc(var(--progress) * 360deg),
    rgba(30,41,59,0.10)   360deg
  );
  transition: background 0.85s cubic-bezier(0.4,0,0.2,1);
}
.tz-card-inner {
  border-radius: 18px;
  background: linear-gradient(145deg, #f0f6ff 0%, #e8f0fb 100%);
  display: flex;
  align-items: center;
  padding: 20px 22px;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.8);
}

/* digital — left */
.tz-clock { display: flex; flex-direction: column; gap: 7px; flex: 1; min-width: 0; }
.tz-clock__time { display: flex; align-items: flex-end; gap: 7px; line-height: 1; }
.tz-hm {
  font-size: 38px; font-weight: 800; color: #1e293b;
  letter-spacing: -1px; font-variant-numeric: tabular-nums; line-height: 1;
}
.tz-clock__sub { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; padding-bottom: 5px; }
.tz-ss  { font-size: 13px; font-weight: 700; color: rgba(30,41,59,0.40); font-variant-numeric: tabular-nums; line-height: 1; }
.tz-ss em { font-style: normal; font-size: 9px; font-weight: 600; margin-left: 1px; opacity: 0.7; }
.tz-ampm { font-size: 11px; font-weight: 700; color: rgba(30,41,59,0.35); letter-spacing: 0.5px; line-height: 1; }
.tz-clock__date { font-size: 11px; color: rgba(30,41,59,0.40); font-weight: 500; letter-spacing: 0.2px; }

/* digital — divider */
.tz-divider { width: 1px; height: 60px; background: rgba(30,41,59,0.10); margin: 0 18px; flex-shrink: 0; border-radius: 1px; }

/* digital — right */
.tz-right { display: flex; flex-direction: column; gap: 10px; align-items: flex-end; }
.tz-tabs  { display: flex; gap: 5px; }
.tz-tab {
  display: flex; align-items: center; gap: 4px;
  padding: 5px 10px; border-radius: 8px;
  border: 1px solid rgba(30,41,59,0.12);
  background: rgba(30,41,59,0.05);
  color: rgba(30,41,59,0.40);
  font-size: 10px; font-weight: 700; letter-spacing: 0.5px;
  cursor: pointer; transition: all 0.18s ease;
}
.tz-tab:hover { background: rgba(30,41,59,0.10); color: rgba(30,41,59,0.70); }
.tz-tab--active { font-weight: 800; }
.tz-tab__flag   { font-size: 12px; }
.tz-meta  { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; }
.tz-city  { font-size: 10px; color: rgba(30,41,59,0.38); font-weight: 500; letter-spacing: 0.4px; text-transform: uppercase; }
.tz-offset { font-size: 12px; font-weight: 700; letter-spacing: 0.3px; transition: color 0.3s ease; }

/* ══════════ ANALOG ══════════ */
.tz-clocks-row {
  display: flex;
  gap: 30px;
  align-items: flex-start;
  justify-content: space-between;
}
.tz-clock-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.tz-analog-outer {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  padding: 3px;
  background: conic-gradient(
    from -90deg,
    var(--accent)              0deg,
    var(--accent)              calc(var(--progress) * 360deg),
    rgba(30,41,59,0.10)        calc(var(--progress) * 360deg),
    rgba(30,41,59,0.10)        360deg
  );
  transition: background 0.85s cubic-bezier(0.4,0,0.2,1);
  box-shadow: 0 4px 16px rgba(0,0,0,0.10);
}
.tz-face {
  width: 100%; height: 100%;
  display: block;
  border-radius: 50%;
  overflow: hidden;
}
.tz-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}
.tz-label__key  { font-size: 11px; font-weight: 800; letter-spacing: 0.5px; }
.tz-label__city { font-size: 10px; color: #94a3b8; font-weight: 500; }
.tz-label__time { font-size: 11px; font-weight: 700; color: #475569; font-variant-numeric: tabular-nums; }
</style>
